// src/components/GlobalNotes.js
import React, { useEffect, useState, useRef, useCallback } from "react";

import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from "../firebase";
import {
  collection,
  addDoc,
  query,
  onSnapshot,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";

import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";

// Theme styles for Lexical
const theme = {
  paragraph: "my-paragraph-class",
  text: {
    bold: "font-bold",
    italic: "italic",
    underline: "underline",
  },
};

function Placeholder() {
  return <div style={{ color: "#888" }}>Start writing your note...</div>;
}

/**
 * LexicalEditor component
 * - Receives initialEditorState as JSON string or null
 * - onChange returns editorState as JSON string
 */
function LexicalEditor({ initialEditorState, onChange, disabled }) {
  const initialConfig = {
    namespace: "MyNotesEditor",
    theme,
    editorState: initialEditorState || null,
    onError(error) {
      console.error(error);
    },
    editable: !disabled,
  };

  const onEditorChange = useCallback(
    (editorState) => {
      editorState.read(() => {
        if (!onChange) return;
        try {
          const jsonString = JSON.stringify(editorState.toJSON());
          onChange(jsonString);
        } catch (e) {
          console.error("Failed to serialize editor state:", e);
        }
      });
    },
    [onChange]
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div style={{ border: "1px solid #ccc", borderRadius: 8, minHeight: 220, padding: 8 }}>
        <RichTextPlugin
          contentEditable={<ContentEditable className="editor-input" style={{ minHeight: 200, outline: "none" }} />}
          placeholder={<Placeholder />}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <OnChangePlugin onChange={onEditorChange} />
      </div>
    </LexicalComposer>
  );
}

function calculateWordCountFromJsonString(jsonString) {
  if (!jsonString) return 0;
  try {
    const json = JSON.parse(jsonString);
    if (!json.root?.children) return 0;
    let text = "";
    json.root.children.forEach((p) => {
      if (p.children) {
        p.children.forEach((child) => {
          if (child.text) text += child.text + " ";
        });
        text += "\n";
      }
    });
    return text.trim().split(/\s+/).filter(Boolean).length;
  } catch {
    return 0;
  }
}

// Utility to get preview text from Lexical JSON string
function getPreviewText(jsonString, maxLength = 60) {
  if (!jsonString) return "No content";
  try {
    const json = JSON.parse(jsonString);
    let text = "";
    if (json.root?.children) {
      json.root.children.forEach((p) => {
        if (p.children) {
          p.children.forEach((child) => {
            if (child.text) text += child.text + " ";
          });
          text += "\n";
        }
      });
    }
    text = text.trim().replace(/\s+/g, " ");
    if (text.length <= maxLength) return text || "No content";
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(" ");
    if (lastSpace > maxLength * 0.7) return truncated.substring(0, lastSpace) + "...";
    return truncated + "...";
  } catch {
    return "No content";
  }
}

export default function GlobalNotes() {
  // UI state variables
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [notes, setNotes] = useState([]);
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(null); // Lexical editorState as JSON string
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Loading states
  const [authLoading, setAuthLoading] = useState(true);
  const [notesLoading, setNotesLoading] = useState(false);

  // Auth user
  const [user, setUser] = useState(null);

  const isUpdatingFromFirebase = useRef(false);
  const saveTimerRef = useRef(null);
  const mountedRef = useRef(true);
  const notesUnsubRef = useRef(null);

  // Filter and sort notes when notes/search/sort change
  useEffect(() => {
    let filtered = [...notes];
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((note) => {
        return (
          (note.title || "").toLowerCase().includes(term) ||
          getPreviewText(note.content, 200).toLowerCase().includes(term)
        );
      });
    }

    // Separate pinned and unpinned notes
    const pinnedNotes = filtered.filter(note => note.pinned);
    const unpinnedNotes = filtered.filter(note => !note.pinned);

    // Sort each group separately
    const sortNotes = (notes) => {
      return notes.sort((a, b) => {
        let aVal, bVal;
        switch (sortBy) {
          case "title":
            aVal = (a.title || "Untitled").toLowerCase();
            bVal = (b.title || "Untitled").toLowerCase();
            break;
          case "createdAt":
            aVal = a.createdAt?.toDate?.() || new Date(0);
            bVal = b.createdAt?.toDate?.() || new Date(0);
            break;
          default:
            aVal = a.updatedAt?.toDate?.() || new Date(0);
            bVal = b.updatedAt?.toDate?.() || new Date(0);
        }
        return sortOrder === "asc" ? (aVal > bVal ? 1 : -1) : aVal < bVal ? 1 : -1;
      });
    };

    setFilteredNotes([...sortNotes(pinnedNotes), ...sortNotes(unpinnedNotes)]);
  }, [notes, searchTerm, sortBy, sortOrder]);

  // Listen for auth state changes
  useEffect(() => {
    setAuthLoading(true);
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthLoading(false);
      if (!u) {
        setNotes([]);
        setFilteredNotes([]);
        setNotesLoading(false);
        setActiveNoteId(null);
        setTitle("");
        setContent(null);
      }
    });
    return () => unsub();
  }, []);

  // Save note with debounce
  const scheduleSave = useCallback(
    (fields = {}) => {
      if (!user || !activeNoteId || !mountedRef.current) return;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);

      saveTimerRef.current = setTimeout(async () => {
        if (!mountedRef.current || isUpdatingFromFirebase.current) return;
        try {
          setSaving(true);
          let payload = { ...fields, updatedAt: serverTimestamp() };
          if (fields.content !== undefined) payload.wordCount = calculateWordCountFromJsonString(fields.content);
          await updateDoc(doc(db, "users", user.uid, "notes", activeNoteId), payload);
        } catch (e) {
          console.error("Autosave failed:", e);
        } finally {
          if (mountedRef.current) setSaving(false);
          saveTimerRef.current = null;
        }
      }, 1200);
    },
    [user, activeNoteId]
  );

  // Subscribe to Firestore notes collection
  useEffect(() => {
    if (notesUnsubRef.current) {
      notesUnsubRef.current();
      notesUnsubRef.current = null;
    }
    if (!user) {
      setNotes([]);
      setFilteredNotes([]);
      setNotesLoading(false);
      return;
    }
    setNotesLoading(true);

    const notesRef = collection(db, "users", user.uid, "notes");
    const q = query(notesRef, orderBy("updatedAt", "desc"));

    notesUnsubRef.current = onSnapshot(
      q,
      (snap) => {
        if (!mountedRef.current) return;
        const docs = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setNotes(docs);
        setNotesLoading(false);

        if (activeNoteId) {
          const activeNote = docs.find((note) => note.id === activeNoteId);
          if (activeNote) {
            // Prevent recursive update triggering save loop
            isUpdatingFromFirebase.current = true;
            setTitle(activeNote.title || "Untitled");
            setContent(activeNote.content || null);
            setTimeout(() => {
              isUpdatingFromFirebase.current = false;
            }, 50);
          } else {
            // Active note deleted externally
            setActiveNoteId(null);
            setTitle("");
            setContent(null);
          }
        }
      },
      (err) => {
        console.error("notes onSnapshot error:", err);
        setNotesLoading(false);
      }
    );

    return () => {
      if (notesUnsubRef.current) {
        notesUnsubRef.current();
        notesUnsubRef.current = null;
      }
    };
  }, [user, activeNoteId]);

  // Manually save note
  const manualSave = async () => {
    if (!user || !activeNoteId) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.uid, "notes", activeNoteId), {
        title,
        content,
        wordCount: calculateWordCountFromJsonString(content),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.error("manual save failed:", err);
      alert("Save failed — see console.");
    } finally {
      setSaving(false);
    }
  };

  // Create new note
  const createNote = async () => {
    if (!user) {
      alert("Sign in to create notes.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: "Untitled",
        content: null,
        wordCount: 0,
        pinned: false, // Add pinned field
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      const ref = await addDoc(collection(db, "users", user.uid, "notes"), payload);
      setActiveNoteId(ref.id);
      setTitle(payload.title);
      setContent(payload.content);
      setOpen(true);
      setMinimized(false);
    } catch (e) {
      console.error("createNote failed:", e);
      alert("Failed to create note — see console.");
    } finally {
      setSaving(false);
    }
  };

  // Toggle pin status of a note
  const togglePin = async (noteId, currentPinned) => {
    if (!user) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "users", user.uid, "notes", noteId), {
        pinned: !currentPinned,
        updatedAt: serverTimestamp(),
      });
    } catch (e) {
      console.error("togglePin failed:", e);
      alert("Failed to pin/unpin note — see console.");
    } finally {
      setSaving(false);
    }
  };

  // Select note from sidebar
  const selectNote = useCallback(
    (note) => {
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
      setActiveNoteId(note.id);
      setTitle(note.title || "Untitled");
      setContent(note.content || null);
      if (!open) setOpen(true);
      if (minimized) setMinimized(false);
    },
    [open, minimized]
  );

  // Delete note
  const deleteNote = async (noteId) => {
    if (!user) return;
    const confirmMsg =
      noteId === activeNoteId
        ? "⚠️ You're about to delete the note you're currently editing! This will close the editor and the note will be permanently deleted. Are you sure?"
        : "Delete this note? This cannot be undone.";
    if (!window.confirm(confirmMsg)) return;

    setSaving(true);
    try {
      await deleteDoc(doc(db, "users", user.uid, "notes", noteId));
      if (noteId === activeNoteId) {
        setActiveNoteId(null);
        setTitle("");
        setContent(null);
      }
    } catch (e) {
      console.error("deleteNote failed:", e);
      alert("Failed to delete note — see console.");
    } finally {
      setSaving(false);
    }
  };

  // Export note as markdown
  const exportNote = (note) => {
    const plainText = getPreviewText(note.content || {}, 1000);
    const exportContent = `# ${note.title || "Untitled"}\n\n${plainText}\n`;
    const blob = new Blob([exportContent], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(note.title || "untitled").replace(/[^a-z0-9]/gi, "_").toLowerCase()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Modal control handlers
  const handleToggleMinimize = () => setMinimized(!minimized);
  const handleClose = () => {
    setOpen(false);
    setMinimized(false);
  };
  const handleMainButtonClick = () => {
    if (!open) {
      setOpen(true);
      setMinimized(false);
      if (!activeNoteId && filteredNotes.length > 0) {
        selectNote(filteredNotes[0]);
      }
    } else if (minimized) {
      setMinimized(false);
    } else {
      setMinimized(true);
    }
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyboard = (e) => {
      if (!open) return;
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case "s":
            e.preventDefault();
            manualSave();
            break;
          case "n":
            e.preventDefault();
            createNote();
            break;
          case "f":
            e.preventDefault();
            if (!minimized) document.querySelector(".search-input")?.focus();
            break;
          case "m":
            e.preventDefault();
            handleToggleMinimize();
            break;
        }
      }
      if (e.key === "Escape") {
        minimized ? handleClose() : handleToggleMinimize();
      }
    };
    if (open) {
      document.addEventListener("keydown", handleKeyboard);
      return () => document.removeEventListener("keydown", handleKeyboard);
    }
  }, [open, minimized]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      if (notesUnsubRef.current) notesUnsubRef.current();
    };
  }, []);

  // Format updatedAt timestamp nicely
  const prettyTime = (ts) => {
    if (!ts) return "—";
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      const diff = Date.now() - d.getTime();
      if (diff < 60000) return "Just now";
      if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
      if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
      if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
      return d.toLocaleDateString();
    } catch {
      return "—";
    }
  };

  // Simple loading spinner
  const LoadingSpinner = () => (
    <div className="flex justify-center items-center">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
    </div>
  );

  if (authLoading) {
    return (
      <button
        className="fixed bottom-6 right-6 w-14 h-14 rounded-full bg-gray-400 text-white shadow-lg flex items-center justify-center z-50 cursor-not-allowed"
        disabled
      >
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white" />
      </button>
    );
  }

  return (
    <>
      {/* Floating button */}
      <button
        onClick={handleMainButtonClick}
        className={`fixed bottom-6 right-6 w-14 h-14 rounded-full ${open && !minimized
          ? "bg-gradient-to-r from-purple-600 to-blue-600"
          : "bg-gradient-to-r from-blue-600 to-purple-600"
          } text-white shadow-lg flex items-center justify-center hover:from-blue-700 hover:to-purple-700 z-50 transition-all duration-200`}
        title={!open ? `Open Notes (${notes.length})` : minimized ? "Maximize Notes" : "Minimize Notes"}
      >
        {!open ? "📝" : minimized ? "📝" : "−"}
        {notes.length > 0 && (
          <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {notes.length > 99 ? "99+" : notes.length}
          </span>
        )}
      </button>

      {/* Minimized */}
      {open && minimized && activeNoteId && (
        <div className="fixed bottom-24 right-6 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800 p-3 z-40 max-w-xs">
          <div className="flex items-center justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate text-gray-800 dark:text-gray-100">{title || "Untitled"}</div>
              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{getPreviewText(content)}</div>
            </div>
            <div className="flex gap-1">
              <button onClick={handleToggleMinimize} className="text-gray-400 hover:text-blue-600 text-sm" title="Maximize">
                ⬆
              </button>
              <button onClick={handleClose} className="text-gray-400 hover:text-red-600 text-sm" title="Close">
                ✕
              </button>
            </div>
          </div>
          {saving && <div className="text-xs text-blue-600 mt-1">Saving...</div>}
        </div>
      )}

      {/* Main Overlay */}
      {open && !minimized && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-[95%] md:w-4/5 lg:w-4/5 xl:w-3/4 h-[90%] p-6 relative flex flex-col">
            {/* Header controls */}
            <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
              <button onClick={handleToggleMinimize} className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 text-xl" title="Minimize (Ctrl+M)">
                −
              </button>
              <button onClick={handleClose} className="text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 text-xl" title="Close notes">
                ✕
              </button>
            </div>

            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">My Notes</h2>
                <p className="text-sm text-gray-500 mt-1">
                  {notesLoading ? "Loading notes..." : `${notes.length} ${notes.length === 1 ? "note" : "notes"}${searchTerm ? ` • ${filteredNotes.length} filtered` : ""}`}
                </p>
              </div>
              <div className="flex items-center gap-3 pr-16">
                <button
                  onClick={createNote}
                  disabled={!user || saving}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium disabled:opacity-50"
                >
                  {saving ? <LoadingSpinner /> : "+ New Note"}
                </button>
              </div>
            </div>

            <div className="flex gap-6 flex-1 overflow-hidden">
              {/* Sidebar */}
              <div className="w-1/3 flex flex-col overflow-hidden">
                <div className="mb-4 space-y-3">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search notes..."
                    className="search-input w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-800 dark:text-gray-100"
                    disabled={notesLoading}
                  />
                  <div className="flex gap-2">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 dark:border-gray-700 rounded text-sm focus:ring-2 focus:ring-blue-400 outline-none bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100"
                      disabled={notesLoading}
                    >
                      <option value="updatedAt" className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100">Last modified</option>
                      <option value="createdAt" className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100">Date created</option>
                      <option value="title" className="bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-100">Title</option>
                    </select>
                    <button
                      onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
                      className="px-2 py-1 border border-gray-300 dark:border-gray-700 rounded text-sm text-gray-700 dark:text-gray-100 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                      title={`Sort ${sortOrder === "asc" ? "descending" : "ascending"}`}
                      disabled={notesLoading}
                    >
                      {sortOrder === "asc" ? "↑" : "↓"}
                    </button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto space-y-2">
                  {notesLoading ? (
                    <div className="text-center py-8">
                      <LoadingSpinner />
                      <p className="text-gray-400 dark:text-gray-500 mt-2">Loading your notes...</p>
                    </div>
                  ) : filteredNotes.length === 0 ? (
                    <div className="text-center py-8">
                      {searchTerm ? (
                        <div>
                          <p className="text-gray-400 mb-2">No notes match your search</p>
                          <button onClick={() => setSearchTerm("")} className="text-blue-600 hover:text-blue-700 text-sm">
                            Clear search
                          </button>
                        </div>
                      ) : (
                        <p className="text-gray-400 dark:text-gray-500">No notes yet. Create your first one!</p>
                      )}
                    </div>
                  ) : (
                    filteredNotes.map((note, index) => {
                      // Add section header for first pinned note and first unpinned note
                      const showPinnedHeader = index === 0 && note.pinned;
                      const showUnpinnedHeader =
                        index === 0 ? !note.pinned :
                          filteredNotes[index - 1].pinned && !note.pinned;

                      return (
                        <React.Fragment key={note.id}>
                          {showPinnedHeader && (
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 mb-2">
                              📌 Pinned Notes
                            </div>
                          )}
                          {showUnpinnedHeader && (
                            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider px-2 mb-2 mt-4">
                              Other Notes
                            </div>
                          )}
                          <div
                            onClick={() => selectNote(note)}
                            className={`p-3 rounded-xl cursor-pointer transition-all duration-200 border ${activeNoteId === note.id
                              ? "bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900 dark:to-purple-900 border-blue-200 dark:border-blue-700 shadow-md"
                              : "bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border-transparent hover:shadow-sm"
                              }`}
                          >
                            <div className="flex justify-between items-start mb-2">
                              <div className="font-medium truncate text-gray-900 dark:text-gray-100 flex-1 pr-2">
                                {note.title || "Untitled"}
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    togglePin(note.id, note.pinned);
                                  }}
                                  className={`text-gray-400 hover:text-blue-600 transition-colors ${note.pinned ? "text-blue-600" : ""
                                    }`}
                                  title={note.pinned ? "Unpin note" : "Pin note"}
                                >
                                  📌
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    exportNote(note);
                                  }}
                                  className="text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Export as Markdown"
                                >
                                  📤
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteNote(note.id);
                                  }}
                                  className="text-gray-400 hover:text-red-600 transition-colors"
                                  title="Delete note"
                                >
                                  🗑
                                </button>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">{getPreviewText(note.content)}</div>
                            <div className="flex justify-between items-center text-xs text-gray-400 dark:text-gray-400">
                              <span>{prettyTime(note.updatedAt)}</span>
                            </div>
                          </div>
                        </React.Fragment>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Editor */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {activeNoteId ? (
                  <>
                    <div className="mb-4">
                      <input
                        value={title}
                        onChange={(e) => {
                          setTitle(e.target.value);
                          scheduleSave({ title: e.target.value });
                        }}
                        placeholder="Note title..."
                        className="w-full text-xl font-semibold border-none outline-none focus:ring-0 px-0 bg-transparent placeholder-gray-400 dark:placeholder-gray-500 dark:text-gray-100"
                      />
                    </div>
                    <LexicalEditor
                      key={activeNoteId || 'new'}
                      initialEditorState={content}
                      onChange={(jsonString) => {
                        setContent(jsonString);
                        scheduleSave({ content: jsonString });
                      }}
                    />


                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-gray-700 dark:text-gray-300">
                          Last saved:{" "}
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            {(() => {
                              const found = notes.find((x) => x.id === activeNoteId);
                              return found ? prettyTime(found.updatedAt) : "—";
                            })()}
                          </span>
                        </span>
                        <span className="text-gray-700 dark:text-gray-300">
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            {calculateWordCountFromJsonString(content)} words
                          </span>
                        </span>
                        {saving && <span className="text-blue-600 dark:text-blue-400">Saving...</span>}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => exportNote({ id: activeNoteId, title, content })}
                          className="px-3 py-1 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                        >
                          Export
                        </button>
                        <button
                          onClick={() => deleteNote(activeNoteId)}
                          className="px-3 py-1 border border-red-300 dark:border-red-700 rounded-lg text-red-700 dark:text-red-400 hover:bg-red-100 dark:hover:bg-gray-800 transition-colors"
                          disabled={saving}
                        >
                          Delete
                        </button>
                        <button
                          onClick={manualSave}
                          className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200"
                          disabled={saving}
                        >
                          {saving ? <LoadingSpinner /> : "Save Now"}
                        </button>
                      </div>
                    </div>

                  </>
                ) : (
                  <div className="flex items-center justify-center h-full text-center">
                    <div className="max-w-md">
                      {notesLoading ? (
                        <>
                          <LoadingSpinner />
                          <p className="text-gray-500 dark:text-gray-400 mt-4">Loading your notes...</p>
                        </>
                      ) : (
                        <>
                          <div className="text-6xl mb-4">✍️</div>
                          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-100 mb-2">Start Writing</h3>
                          <p className="text-gray-500 dark:text-gray-400 mb-6">Select a note from the sidebar or create a new one to begin writing.</p>
                          <button
                            onClick={createNote}
                            disabled={!user || saving}
                            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-200 font-medium disabled:opacity-50"
                          >
                            {saving ? <LoadingSpinner /> : "Create Your First Note"}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
